import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ProfileAvatar } from "../components/ObserverProfileBits";
import { observerApi } from "../services/observerApi";
import { ObserverSearchResult } from "../types/observer";
import SeoHead from "../components/SeoHead";

export default function ObserverSearchPage() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [rows, setRows] = useState<ObserverSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const value = params.get("q") || "";
    if (!value) {
      setLoading(true);
      observerApi
        .directory("", 1, 24)
        .then((directory) => setRows(directory.items || []))
        .catch(() => setRows([]))
        .finally(() => setLoading(false));
      return;
    }
    setLoading(true);
    observerApi
      .search(value)
      .then((value) => setRows(value || []))
      .finally(() => setLoading(false));
  }, [params]);
  return (
    <div className="ot-page">
      <SeoHead
        title={
          params.get("q")
            ? "Practitioner Name Search | Observer Theory"
            : "Search and Browse Spiritual Practitioners | Observer Theory"
        }
        description="Search sourced public practitioner records by name, including exact-name Tier B records, or browse indexable Tier A manifestation, tarot, psychic, mediumship, astrology, and spiritual-practice profiles."
        path="/search"
        noIndex={Boolean(params.get("q"))}
      />
      <header className="ot-page-head">
        <span className="section-kicker">
          Search the spiritual-practice record
        </span>
        <h1>Who is behind the teaching, reading, or practice?</h1>
        <p>
          Search an exact name, alias, former name, or spelling variation.
          Observer Theory covers manifestation and law of attraction coaches,
          law of assumption teachers, tarot and oracle readers, psychics,
          mediums, astrologers, energy practitioners, spiritual teachers, and
          related public practices.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setParams({ q });
          }}
        >
          <input
            aria-label="Practitioner name"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Teacher, coach, reader, psychic, medium, or brand"
          />
          <button>Search by name</button>
        </form>
        <small>
          Search is name-first. Observer Theory does not rank spiritual beliefs,
          promise outcomes, or recommend a person based on popularity.
        </small>
      </header>
      {loading && <p>Searching the record…</p>}
      {!loading && !params.get("q") && rows.length > 0 && (
        <div className="ot-directory-summary">
          <p>Published records, shown alphabetically</p>
          <Link to="/practitioners">Browse by practice category</Link>
        </div>
      )}
      {!loading && params.get("q") && rows.length === 0 && (
        <div className="ot-empty">
          <h2>No published record found</h2>
          <p>
            Unknown is an honest state. If this is a public spiritual teacher,
            manifestation coach, reader, psychic, medium, astrologer, or related
            practitioner, you can nominate them for research and human review.
          </p>
          <Link className="button button-ink" to="/report?kind=nomination">
            Nominate this practitioner
          </Link>
        </div>
      )}
      <div className="ot-results">
        {rows.map((r) => (
          <Link
            key={r.entity.publicId}
            to={"/profile/" + r.entity.publicId}
            className="ot-result"
          >
            <ProfileAvatar
              entity={r.entity}
              size="small"
              linkToSource={false}
            />
            <span>{r.entity.entityType}</span>
            <h2>{r.entity.displayName}</h2>
            <p>{r.entity.practiceTags.join(" · ")}</p>
            <small>
              Why this result? {r.why}. {r.recordFacts} sourced record facts;{" "}
              {r.verifiedReviewDepth} verified reviews.
            </small>
          </Link>
        ))}
      </div>
    </div>
  );
}
